/**
 * The controls of the LoD2 view, as quiet cards instead of a row of raw inputs. Two parts: the
 * palette that slides in at the side of the map (drawing, surroundings, light) and, under the map,
 * the own 3D model. Pure presentation — the state and every action live in Building3DView and come
 * in as properties.
 */
import { useTranslation } from "react-i18next";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Loader2,
  RotateCcw,
  RotateCw,
  Sun,
  Trees,
  Box,
  Upload,
  Trash2,
  PenLine,
  Square,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import type { ColourMode } from "./modelAppearance";

export interface ControlsPlacement {
  lon: number;
  lat: number;
  altitude: number;
  rotationDeg: number;
  source?: string;
}

export type DrawMode = "rectangle" | "polygon" | null;

interface Properties {
  /** `palette`: Zeichnen, Umgebung, Licht (an der Seite der Karte); `bottom`: das eigene Modell (unter der Karte). */
  part: "palette" | "bottom";

  drawMode: DrawMode;
  onDrawMode: (mode: DrawMode) => void;
  drawPoints: number;
  onDrawFinish: () => void;
  snapRightAngles: boolean;
  onSnapRightAngles: (on: boolean) => void;
  /** Ausgewählter gezeichneter Baukörper: verschieben (Meter) und drehen (Grad). */
  selectedBodyName: string | null;
  onBodyNudge: (east: number, north: number) => void;
  onBodyRotate: (degrees: number) => void;

  openSpace: boolean;
  onOpenSpace: (on: boolean) => void;
  openSpaceLoading: boolean;
  openSpaceInfo: string | null;
  setbacks: boolean;
  onSetbacks: (on: boolean) => void;

  vegetation: { status: string; estimatedCount?: number } | null;
  treeCount: number;
  shrubCount: number;
  vegetationStarting: boolean;
  onStartVegetation: () => void;

  parcels: boolean;
  onParcels: (on: boolean) => void;
  parcelsLoading: boolean;
  parcelCount: number | null;

  sealed: boolean;
  onSealed: (on: boolean) => void;
  sealedLoading: boolean;
  /** Befestigte Fläche in m², null solange nichts geladen ist; `sealedMissing`: keine Analyse am Ort. */
  sealedArea: number | null;
  sealedMissing: boolean;

  shadows: boolean;
  onShadows: (on: boolean) => void;
  sunDate: Date;
  onSunDate: (date: Date) => void;
  sunBelowHorizon: boolean;

  model: { name: string; georeferenceIgnored: { distanceM: number } | null } | null;
  draft: ControlsPlacement | null;
  unsaved: boolean;
  modelBusy: boolean;
  modelError: string | null;
  colourMode: ColourMode;
  colourModesAvailable: boolean;
  onColourMode: (mode: ColourMode) => void;
  onNudge: (east: number, north: number) => void;
  onRotate: (deltaDeg: number) => void;
  onLift: (deltaM: number) => void;
  onSave: () => void;
  onRemove: () => void;
  onUpload: () => void;
}

/** The four days a shadow study is usually read on. */
const SEASONS: Array<{ key: string; month: number; day: number }> = [
  { key: "spring", month: 2, day: 20 },
  { key: "summer", month: 5, day: 21 },
  { key: "autumn", month: 8, day: 23 },
  { key: "winter", month: 11, day: 21 },
];

const Card = (properties: { icon: any; title: string; children: any; aside?: any }) => (
  <section className="flex min-w-0 flex-col gap-3 rounded-lg border bg-card p-4">
    <header className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <properties.icon aria-hidden className="h-4 w-4 text-muted-foreground" />
        {properties.title}
      </div>
      {properties.aside}
    </header>
    {properties.children}
  </section>
);

const Segmented = <T extends string>(properties: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  disabled?: boolean;
}) => (
  <div role="radiogroup" className="inline-flex w-full rounded-md border bg-muted p-0.5">
    {properties.options.map((option) => {
      const active = option.value === properties.value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={active}
          disabled={properties.disabled}
          onClick={() => properties.onChange(option.value)}
          className={
            "flex-1 rounded px-2 py-1 text-xs transition-colors disabled:opacity-50 " +
            (active ? "bg-background font-semibold shadow-sm" : "text-muted-foreground hover:text-foreground")
          }
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const IconButton = (properties: { label: string; onClick: () => void; children: any; disabled?: boolean }) => (
  <Button
    type="button"
    size="icon"
    variant="outline"
    className="h-8 w-8"
    aria-label={properties.label}
    title={properties.label}
    disabled={properties.disabled}
    onClick={properties.onClick}
  >
    {properties.children}
  </Button>
);

/** Dieselbe Farbe wie die Ebene am Boden (Building3DView) und die Karte von land.greenspace. */
export const SEALED_SWATCH = "#B08968";

const hhmm = (date: Date) => `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

export default function Building3DControls(properties: Properties) {
  const { t } = useTranslation();
  const p = properties;
  const hour = p.sunDate.getHours() + p.sunDate.getMinutes() / 60;
  const season = SEASONS.find((s) => s.month === p.sunDate.getMonth() && s.day === p.sunDate.getDate())?.key;

  const surroundings = (
      <Card icon={Trees} title={t("building3D.ui.surroundings")}>
  
        {!p.vegetation || p.vegetation.status === "none" ? (
          <>
            <p className="text-xs text-muted-foreground">{t("building3D.ui.vegetationIntro")}</p>
            <Button size="sm" variant="outline" className="w-fit" disabled={p.vegetationStarting} onClick={p.onStartVegetation}>
              {t("building3D.vegetationStart")}
            </Button>
          </>
        ) : null}
        {p.vegetation?.status === "pending" ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
            {t("building3D.vegetationPending")}
          </p>
        ) : null}
        {p.vegetation?.status === "failed" ? (
          <>
            <p className="text-xs text-destructive">{t("building3D.vegetationFailed")}</p>
            <Button size="sm" variant="outline" className="w-fit" disabled={p.vegetationStarting} onClick={p.onStartVegetation}>
              {t("building3D.vegetationRetry")}
            </Button>
          </>
        ) : null}
        <div className="flex items-center justify-between gap-2 border-b pb-3 text-sm">
          <span className="flex items-center gap-2">
            {t("building3D.ui.parcels")}
            {p.parcelsLoading ? <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : null}
            {p.parcels && !p.parcelsLoading && p.parcelCount !== null ? (
              <span className="text-xs text-muted-foreground">{t("building3D.ui.parcelCount", { count: p.parcelCount })}</span>
            ) : null}
          </span>
          <Switch checked={p.parcels} onCheckedChange={p.onParcels} aria-label={t("building3D.ui.parcels")} />
        </div>
        <div className="flex items-center justify-between gap-2 border-b pb-3 text-sm">
          <span className="flex items-center gap-2">
            <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: SEALED_SWATCH }} />
            {t("building3D.ui.sealed")}
            {p.sealedLoading ? <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : null}
            {p.sealed && !p.sealedLoading && p.sealedArea !== null && !p.sealedMissing ? (
              <span className="text-xs text-muted-foreground">{t("building3D.ui.sealedArea", { area: p.sealedArea.toLocaleString("de-DE") })}</span>
            ) : null}
          </span>
          <Switch checked={p.sealed} onCheckedChange={p.onSealed} aria-label={t("building3D.ui.sealed")} />
        </div>
        {p.sealed && !p.sealedLoading && p.sealedMissing ? <p className="-mt-1 text-xs text-muted-foreground">{t("building3D.ui.sealedNone")}</p> : null}
        <div className="flex items-center justify-between gap-2 border-b pb-3 text-sm">
          <span className="flex items-center gap-2">
            {t("building3D.ui.openSpace")}
            {p.openSpaceLoading ? <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : null}
          </span>
          <Switch checked={p.openSpace} onCheckedChange={p.onOpenSpace} aria-label={t("building3D.ui.openSpace")} />
        </div>
        {p.openSpaceInfo ? <p className="-mt-1 text-xs text-muted-foreground">{p.openSpaceInfo}</p> : null}
        <div className="flex items-center justify-between gap-2 border-b pb-3 text-sm">
          <span>{t("building3D.ui.setbacks")}</span>
          <Switch checked={p.setbacks} onCheckedChange={p.onSetbacks} aria-label={t("building3D.ui.setbacks")} />
        </div>
        {p.vegetation?.status === "ready" ? (
          <div className="flex flex-col gap-1 text-xs">
            <span>
              {p.treeCount + p.shrubCount === 0
                ? t("building3D.vegetationEmpty")
                : t("building3D.ui.vegetationCounts", { trees: p.treeCount, shrubs: p.shrubCount })}
            </span>
            <span className="text-muted-foreground">
              {(p.vegetation.estimatedCount ?? 0) > 0 ? t("building3D.ui.vegetationEstimated") : t("building3D.ui.vegetationMeasured")}
            </span>
          </div>
        ) : null}
      </Card>
  );

  const light = (
      <Card icon={Sun} title={t("building3D.ui.light")} aside={<Switch checked={p.shadows} onCheckedChange={p.onShadows} aria-label={t("building3D.shadows")} />}>
        {!p.shadows ? (
          <p className="text-xs text-muted-foreground">{t("building3D.ui.shadowsIntro")}</p>
        ) : (
          <>
            <Segmented
              value={season ?? ""}
              options={SEASONS.map((s) => ({ value: s.key, label: t(`building3D.ui.season_${s.key}`) }))}
              onChange={(key) => {
                const s = SEASONS.find((x) => x.key === key)!;
                p.onSunDate(new Date(p.sunDate.getFullYear(), s.month, s.day, p.sunDate.getHours(), p.sunDate.getMinutes()));
              }}
            />
            <div className="flex items-center gap-3">
              <Slider
                min={4}
                max={22}
                step={0.25}
                value={[hour]}
                onValueChange={([value]) =>
                  p.onSunDate(new Date(p.sunDate.getFullYear(), p.sunDate.getMonth(), p.sunDate.getDate(), Math.floor(value), Math.round((value % 1) * 60)))
                }
                aria-label={t("building3D.shadowsTime")}
              />
              <span className="w-12 shrink-0 text-right text-sm tabular-nums">{hhmm(p.sunDate)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {p.sunBelowHorizon
                ? t("building3D.shadowsNight")
                : t("building3D.ui.sunOn", { date: p.sunDate.toLocaleDateString(undefined, { day: "numeric", month: "long" }) })}
            </p>
          </>
        )}
      </Card>
  );

  const model = (
      <Card icon={Box} title={t("building3D.ui.model")}>
        {!p.model ? (
          <>
            <p className="text-xs text-muted-foreground">{t("building3D.ui.modelIntro")}</p>
            <Button size="sm" variant="outline" className="w-fit gap-2" disabled={p.modelBusy} onClick={p.onUpload}>
              {p.modelBusy ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : <Upload aria-hidden className="h-4 w-4" />}
              {p.modelBusy ? t("building3D.modelUploading") : t("building3D.ui.upload")}
            </Button>
          </>
        ) : (
          <>
            <div className="min-w-0 text-xs">
              <div className="truncate font-medium" title={p.model.name}>{p.model.name}</div>
              <div className="text-muted-foreground">
                {p.draft?.source ? t(`building3D.modelSource_${p.unsaved ? "manual" : p.draft.source}`) : null}
                {p.model.georeferenceIgnored
                  ? ` · ${t("building3D.modelGeorefIgnored", { km: Math.round(p.model.georeferenceIgnored.distanceM / 1000) })}`
                  : ""}
              </div>
            </div>
  
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t("building3D.ui.colours")}</span>
              <Segmented<ColourMode>
                value={p.colourMode}
                disabled={!p.colourModesAvailable}
                options={[
                  { value: "original", label: t("building3D.ui.colourOriginal") },
                  { value: "natural", label: t("building3D.ui.colourNatural") },
                  { value: "house", label: t("building3D.ui.colourHouse") },
                ]}
                onChange={p.onColourMode}
              />
              {!p.colourModesAvailable ? <span className="text-xs text-muted-foreground">{t("building3D.ui.coloursOnlyIfc")}</span> : null}
            </div>
  
            {p.draft ? (
              <div className="flex flex-wrap items-center gap-4">
                <div className="grid grid-cols-3 gap-1" aria-label={t("building3D.modelMove")}>
                  <span />
                  <IconButton label={t("building3D.ui.north")} onClick={() => p.onNudge(0, 1)}><ArrowUp className="h-4 w-4" /></IconButton>
                  <span />
                  <IconButton label={t("building3D.ui.west")} onClick={() => p.onNudge(-1, 0)}><ArrowLeft className="h-4 w-4" /></IconButton>
                  <span />
                  <IconButton label={t("building3D.ui.east")} onClick={() => p.onNudge(1, 0)}><ArrowRight className="h-4 w-4" /></IconButton>
                  <span />
                  <IconButton label={t("building3D.ui.south")} onClick={() => p.onNudge(0, -1)}><ArrowDown className="h-4 w-4" /></IconButton>
                  <span />
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex gap-1">
                    <IconButton label={t("building3D.ui.rotateLeft")} onClick={() => p.onRotate(5)}><RotateCcw className="h-4 w-4" /></IconButton>
                    <IconButton label={t("building3D.ui.rotateRight")} onClick={() => p.onRotate(-5)}><RotateCw className="h-4 w-4" /></IconButton>
                  </div>
                  <div className="flex gap-1">
                    <IconButton label={t("building3D.ui.lower")} onClick={() => p.onLift(-0.5)}><ChevronDown className="h-4 w-4" /></IconButton>
                    <IconButton label={t("building3D.ui.raise")} onClick={() => p.onLift(0.5)}><ChevronUp className="h-4 w-4" /></IconButton>
                  </div>
                </div>
                <p className="min-w-[140px] flex-1 text-xs text-muted-foreground">{t("building3D.modelDragHint")}</p>
              </div>
            ) : null}
  
            <div className="flex items-center justify-between gap-2">
              <Button size="sm" disabled={p.modelBusy || !p.unsaved} onClick={p.onSave}>
                {p.unsaved ? t("building3D.modelSave") : t("building3D.modelSaved")}
              </Button>
              <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground" disabled={p.modelBusy} onClick={p.onRemove}>
                <Trash2 aria-hidden className="h-4 w-4" />
                {t("building3D.modelRemove")}
              </Button>
            </div>
          </>
        )}
        {p.modelError ? <p className="text-xs text-destructive">{p.modelError}</p> : null}
      </Card>
  );

  const drawing = (
    <Card icon={PenLine} title={t("building3D.massing.draw")}>
      <div className="grid grid-cols-2 gap-1.5">
        <Button size="sm" variant={p.drawMode === "rectangle" ? "default" : "outline"} onClick={() => p.onDrawMode(p.drawMode === "rectangle" ? null : "rectangle")}>
          <Square aria-hidden className="mr-1.5 h-3.5 w-3.5" />
          {t("building3D.massing.rectangle")}
        </Button>
        <Button size="sm" variant={p.drawMode === "polygon" ? "default" : "outline"} onClick={() => p.onDrawMode(p.drawMode === "polygon" ? null : "polygon")}>
          <PenLine aria-hidden className="mr-1.5 h-3.5 w-3.5" />
          {t("building3D.massing.polygon")}
        </Button>
      </div>
      {p.drawMode ? (
        <div className="flex flex-col gap-2 rounded-md bg-muted px-3 py-2 text-xs">
          <span>{t(p.drawMode === "rectangle" ? "building3D.massing.hintRectangle" : "building3D.massing.hintPolygon")}</span>
          <div className="flex gap-2">
            {p.drawMode === "polygon" && p.drawPoints >= 3 ? (
              <Button size="sm" variant="outline" className="h-7" onClick={p.onDrawFinish}>
                {t("building3D.massing.finish")}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" className="h-7" onClick={() => p.onDrawMode(null)}>
              {t("building3D.massing.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t("building3D.massing.drawIntro")}</p>
      )}
      {p.selectedBodyName && !p.drawMode ? (
        <div className="flex flex-col gap-2 border-t pt-3">
          <span className="truncate text-xs font-medium" title={p.selectedBodyName}>
            {t("building3D.massing.selected", { name: p.selectedBodyName })}
          </span>
          <div className="flex items-center gap-3">
            <div className="grid grid-cols-3 gap-1" aria-label={t("building3D.modelMove")}>
              <span />
              <IconButton label={t("building3D.ui.north")} onClick={() => p.onBodyNudge(0, 1)}><ArrowUp className="h-4 w-4" /></IconButton>
              <span />
              <IconButton label={t("building3D.ui.west")} onClick={() => p.onBodyNudge(-1, 0)}><ArrowLeft className="h-4 w-4" /></IconButton>
              <span />
              <IconButton label={t("building3D.ui.east")} onClick={() => p.onBodyNudge(1, 0)}><ArrowRight className="h-4 w-4" /></IconButton>
              <span />
              <IconButton label={t("building3D.ui.south")} onClick={() => p.onBodyNudge(0, -1)}><ArrowDown className="h-4 w-4" /></IconButton>
              <span />
            </div>
            <div className="flex gap-1">
              <IconButton label={t("building3D.ui.rotateLeft")} onClick={() => p.onBodyRotate(5)}><RotateCcw className="h-4 w-4" /></IconButton>
              <IconButton label={t("building3D.ui.rotateRight")} onClick={() => p.onBodyRotate(-5)}><RotateCw className="h-4 w-4" /></IconButton>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t("building3D.massing.dragHint")}</p>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 text-sm">
        <span>{t("building3D.massing.snap")}</span>
        <Switch checked={p.snapRightAngles} onCheckedChange={p.onSnapRightAngles} aria-label={t("building3D.massing.snap")} />
      </div>
    </Card>
  );

  if (p.part === "palette") {
    return (
      <div className="flex min-w-0 flex-col gap-3">
        {drawing}
        {surroundings}
        {light}
      </div>
    );
  }

  return (
    <div className="mt-3 grid min-w-0 gap-3">
      {model}
    </div>
  );
}
